import React, { useEffect, useState, useRef, memo } from "react";
import Flex from "./Flex";
import styled from "styled-components";
import Button from "./Button";
import { useHistory, useLocation } from "react-router-dom";
import { FetchResponse } from "../../utils/types";
import axios from "axios";
import { API_ENDPOINT } from "../../utils/constants";

// Custom OTP Input Component with improved functionality
const CustomOtpInput = memo(({ value, onChange, numInputs = 4 }: any) => {
  const [otp, setOtp] = useState(value || "");
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    setOtp(value || "");
  }, [value]);

  const handleChange = (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
    const digit = e.target.value.replace(/\D/g, ''); // Only allow digits
    
    if (digit.length > 1) {
      // Handle paste or multiple digits
      const digits = digit.slice(0, numInputs - index).split('');
      const newOtp = [...otp.split("")];
      digits.forEach((d, i) => {
        if (index + i < numInputs) {
          newOtp[index + i] = d;
        }
      });
      const newOtpString = newOtp.join("").slice(0, numInputs);
      setOtp(newOtpString);
      onChange(newOtpString);
      
      // Focus on the next empty input or last input
      const nextIndex = Math.min(index + digits.length, numInputs - 1);
      inputRefs.current[nextIndex]?.focus();
      return;
    }
    
    if (digit.length === 0) {
      // Handle deletion
      const newOtp = otp.split("");
      newOtp[index] = "";
      const newOtpString = newOtp.join("");
      setOtp(newOtpString);
      onChange(newOtpString);
      return;
    }
    
    // Handle single digit input
    const newOtp = otp.split("");
    newOtp[index] = digit;
    const newOtpString = newOtp.join("");
    setOtp(newOtpString);
    onChange(newOtpString);
    
    // Auto-advance to next input
    if (index < numInputs - 1 && digit) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace") {
      if (!otp[index] && index > 0) {
        // If current input is empty, move to previous and clear it
        inputRefs.current[index - 1]?.focus();
        const newOtp = otp.split("");
        newOtp[index - 1] = "";
        const newOtpString = newOtp.join("");
        setOtp(newOtpString);
        onChange(newOtpString);
      } else if (otp[index]) {
        // If current input has value, clear it
        const newOtp = otp.split("");
        newOtp[index] = "";
        const newOtpString = newOtp.join("");
        setOtp(newOtpString);
        onChange(newOtpString);
      }
    } else if (e.key === "ArrowLeft" && index > 0) {
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === "ArrowRight" && index < numInputs - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text/plain").replace(/\D/g, '').slice(0, numInputs);
    if (pastedData.length > 0) {
      const newOtp = pastedData.padEnd(numInputs, "").split("").slice(0, numInputs);
      const newOtpString = newOtp.join("");
      setOtp(newOtpString);
      onChange(newOtpString);
      
      // Focus on the last filled input or last input
      const focusIndex = Math.min(pastedData.length - 1, numInputs - 1);
      inputRefs.current[focusIndex]?.focus();
    }
  };

  return (
    <OtpContainer>
      {Array.from({ length: numInputs }, (_, index) => (
        <OtpInputField
          key={index}
          ref={(el) => (inputRefs.current[index] = el)}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={otp[index] || ""}
          onChange={(e) => handleChange(index, e)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onPaste={handlePaste}
          autoFocus={index === 0 && !otp}
        />
      ))}
    </OtpContainer>
  );
});

interface EmailOtpVerificationProps {
  email: string;
  onVerifySuccess?: (response: any) => void;
  onVerifyError?: (error: any) => void;
  autoSendOtp?: boolean;
  initialDelay?: number;
  otpAlreadySent?: boolean; // If true, shows OTP input directly (OTP was sent externally)
  isOAuthFlow?: boolean;
  redirectUrl?: string | null;
  mode?: 'signup' | 'login' | 'login_otp';
  password?: string; // For login mode
  onEmailChange?: (newEmail: string) => void;
}

const EmailOtpVerification: React.FC<EmailOtpVerificationProps> = ({
  email,
  onVerifySuccess,
  onVerifyError,
  autoSendOtp = false,
  initialDelay = 0,
  otpAlreadySent = false,
  isOAuthFlow = false,
  redirectUrl = null,
  mode = 'signup',
  password,
  onEmailChange,
}) => {
  const history = useHistory<any>();
  const location = useLocation<any>();
  const [otp, setOtp] = useState<string>("");
  const [isLoading, setLoading] = useState(false);
  const [messages, setMessages] = useState<any>({ type: "", message: "" });
  const [showAlert, setShowAlert] = useState(false);
  const [delay, setDelay] = useState(initialDelay);
  const [otpSent, setOtpSent] = useState(autoSendOtp || initialDelay > 0 || otpAlreadySent);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const delayRef = useRef(0);
  const isTimerRunningRef = useRef(false);
  const [timeDisplay, setTimeDisplay] = useState("0:00");
  const currentSearch = location.search;

  const [isEditingEmail, setIsEditingEmail] = useState(false);
  const [tempEmail, setTempEmail] = useState(email);

  useEffect(() => {
    setTempEmail(email);
    // Don't reset otpSent when email changes externally (e.g., from parent component)
    // Only reset if we're not currently showing OTP input
  }, [email]);

  // Initialize timer when delay changes
  useEffect(() => {
    if (delay > 0 && !isTimerRunningRef.current) {
      delayRef.current = delay;
      isTimerRunningRef.current = true;
      
      const mins = Math.floor(delay / 60);
      const secs = delay % 60;
      setTimeDisplay(`${mins}:${secs < 10 ? `0${secs}` : secs}`);
      
      timerRef.current = setInterval(() => {
        delayRef.current = Math.max(0, delayRef.current - 1);
        
        if (delayRef.current <= 0) {
          if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
          }
          isTimerRunningRef.current = false;
          setTimeDisplay("0:00");
          setDelay(0);
        } else {
          const mins = Math.floor(delayRef.current / 60);
          const secs = delayRef.current % 60;
          setTimeDisplay(`${mins}:${secs < 10 ? `0${secs}` : secs}`);
        }
      }, 1000);
    } else if (delay === 0) {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      isTimerRunningRef.current = false;
      setTimeDisplay("0:00");
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      isTimerRunningRef.current = false;
    };
  }, [delay]);

  // Reset messages when email changes
  useEffect(() => {
    setMessages({ type: "", message: "" });
    setOtp("");
    setShowAlert(false);
  }, [email]);

  // When otpAlreadySent is true, start the timer immediately
  useEffect(() => {
    if (otpAlreadySent && delay === 0) {
      // Start the default 1 minute timer for resend
      setDelay(60);
    }
  }, [otpAlreadySent]);

  // Auto-send OTP if enabled
  useEffect(() => {
    if (autoSendOtp && email && !otpSent) {
      sendOtp();
      setOtpSent(true);
    }
  }, [autoSendOtp, email]);

  const sendOtp = async (targetEmail?: string) => {
    const emailToUse = targetEmail || email;
    if (!emailToUse) {
      console.error("sendOtp: No email provided");
      throw new Error("Email is required");
    }
    console.log("sendOtp: Sending OTP to", emailToUse, "mode:", mode);
    setLoading(true);
    try {
      // Build URL with redirect_to query parameter if available
      let url = API_ENDPOINT + "/otp";
      if (redirectUrl) {
        url += `?redirect_to=${encodeURIComponent(redirectUrl)}`;
      }
      
      console.log("sendOtp: Calling API", url, { email: emailToUse, mode: mode });
      const resp = await axios.post<FetchResponse<{ message: string }>>(
        url,
        { email: emailToUse, mode: mode }  // Pass mode (login or signup) to use correct email template
      );
      const re: any = resp?.data;
      console.log("sendOtp: Success", re);
      // Use default 1 minute (60 seconds) timer for resend OTP
      setDelay(60);
      setOtpSent(true);
      setOtp(""); // Clear previous OTP input
      setMessages({
        type: "success",
        message: re?.message || "OTP has been sent to your email. Please check your inbox.",
      });
      setShowAlert(true);
      setLoading(false);
      
      // Don't call onEmailChange here - let the caller handle it to avoid re-render issues
      return true; // Return success
    } catch (error: any) {
      console.error("sendOtp: Error", error);
      let errorMessage = "Failed to send OTP. Please try again.";
      
      if (error.response?.status === 400) {
        errorMessage = error.response?.data?.message || "Unable to send OTP. Please check your email address and try again.";
      } else if (error.response?.status === 404) {
        errorMessage = error.response?.data?.message || "User not found. Please check your email address.";
      } else if (error.response?.status === 429) {
        errorMessage = error.response?.data?.message || "Too many requests. Please wait before requesting a new OTP.";
      } else if (error.response?.status === 500) {
        errorMessage = "Something went wrong on our end. Please try again in a moment.";
      } else if (!error.response) {
        errorMessage = "Unable to connect. Please check your internet connection and try again.";
      } else if (error.response?.data?.error?.[0]?.email) {
        errorMessage = error.response.data.error[0].email;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      
      setMessages({
        type: "error",
        message: errorMessage,
      });
      setShowAlert(true);
      if (onVerifyError) {
        onVerifyError(error);
      }
      setLoading(false);
      throw error; // Re-throw to allow caller to handle
    }
  };

  const handleVerifyOtp = async () => {
    if (otp.length !== 4) {
      setMessages({
        type: "error",
        message: "Please enter a complete 4-digit OTP code.",
      });
      return;
    }

    // Validate OTP contains only digits
    if (!/^\d{4}$/.test(otp)) {
      setMessages({
        type: "error",
        message: "OTP should only contain numbers. Please enter a valid 4-digit code.",
      });
      return;
    }

    setLoading(true);
    try {
      const resp = await axios.post<FetchResponse<{ message: string }>>(
        API_ENDPOINT + "/verify-otp",
        {
          email: email,
          otp: parseInt(otp) || 0,
        }
      );
      const re: any = resp.data;
      setMessages({
        type: "success",
        message: re?.message || "Email verified successfully!",
      });
      
      if (onVerifySuccess) {
        onVerifySuccess(re);
      } else {
        // Default behavior based on mode
        if ((mode === 'login' || mode === 'login_otp') && re?.data?.refresh_token) {
          // User is logged in, redirect
          handleLoginRedirect(redirectUrl);
        } else if ((mode === 'login' || mode === 'login_otp') && password) {
          // Try login after OTP verification
          tryLoginAfterVerification();
        } else if (mode === 'signup') {
          // For signup, always try to log in user after verification
          if (re?.data?.refresh_token) {
            // User is logged in after signup
            handleSignupRedirect(redirectUrl);
          } else {
            // Try to login user after signup verification
            // This should not happen normally, but handle it gracefully
            handleSignupRedirect(redirectUrl);
          }
        } else {
          // Email verified but need to login (fallback)
          history.push({
            pathname: "/login",
            search: currentSearch,
            state: { 
              response: true, 
              message: "Email verified successfully. Please login to continue.",
              verifiedEmail: email
            },
          });
        }
      }
    } catch (error: any) {
      let errorMessage = "Unable to verify OTP. Please try again.";
      
      if (error.response?.status === 400) {
        // Wrong OTP or expired OTP
        errorMessage = error.response?.data?.message || "The OTP you entered is incorrect or has expired. Please check your email and enter the correct code, or request a new OTP.";
      } else if (error.response?.status === 404) {
        errorMessage = error.response?.data?.message || "Verification service is temporarily unavailable. Please try again later.";
      } else if (error.response?.status === 500) {
        errorMessage = error.response?.data?.message || "Something went wrong on our end. Please try again in a moment.";
      } else if (!error.response) {
        errorMessage = error.response?.data?.message || "Unable to connect. Please check your internet connection and try again.";
      } else if (error.response?.data?.error?.[0]?.otp) {
        errorMessage = error.response?.data?.error?.[0]?.otp || error.response?.data?.message;
      } else if (error.response?.data?.error?.[0]?.email) {
        errorMessage = error.response?.data?.error?.[0]?.email || error.response?.data?.message;
      } else if (error.response?.data?.message) {
        // Check if backend message is user-friendly, otherwise use our default
        const backendMessage = error.response.data.message;
        if (backendMessage.toLowerCase().includes("failed to verify") || 
            backendMessage.toLowerCase().includes("failed")) {
          errorMessage = "The OTP you entered is incorrect or has expired. Please check your email and enter the correct code, or request a new OTP.";
        } else {
          errorMessage = backendMessage;
        }
      }
      
      setMessages({
        type: "error",
        message: errorMessage,
      });
      
      // Clear OTP input on error so user can re-enter
      setOtp("");
      
      if (onVerifyError) {
        onVerifyError(error);
      }
    }
    setLoading(false);
  };

  const tryLoginAfterVerification = async () => {
    if (!password) return;
    try {
      const loginResp = await axios.post<FetchResponse<{ message: string }>>(
        API_ENDPOINT + "/login",
        { email: email, password: password }
      );
      
      setMessages({
        type: "success",
        message: "Email verified and logged in successfully!",
      });
      
      handleLoginRedirect(redirectUrl);
    } catch (error: any) {
      let errorMessage = "Email verified, but login failed. Please try logging in again.";
      
      if (error.response?.status === 401) {
        errorMessage = "Email verified, but the password is incorrect. Please try logging in again.";
      } else if (error.response?.status === 400) {
        errorMessage = error.response?.data?.error?.[0]?.form_level_error || 
                       error.response?.data?.message || 
                       "Email verified, but login failed. Please try again.";
      } else if (!error.response) {
        errorMessage = "Email verified, but unable to connect. Please check your internet connection and try logging in again.";
      } else {
        errorMessage = error.response?.data?.message || errorMessage;
      }
      
      setMessages({
        type: "error",
        message: errorMessage,
      });
    }
  };

  const handleLoginRedirect = (redirectUrl: string | null) => {
    if (redirectUrl) {
      try {
        const redirectTo = new URL(redirectUrl, window.location.origin);
        window.location.href = redirectTo.href;
      } catch (e) {
        history.push({
          pathname: "/",
          state: { response: true, message: "logged in successfully" },
        });
      }
    } else {
      history.push({
        pathname: "/",
        state: { response: true, message: "logged in successfully" },
      });
    }
  };

  const handleSignupRedirect = (redirectUrl: string | null) => {
    if (redirectUrl) {
      try {
        const redirectTo = new URL(redirectUrl, window.location.origin);
        window.location.href = redirectTo.href;
      } catch (e) {
        history.push({
          pathname: "/",
          state: { response: true, message: "Account verified and logged in successfully" },
        });
      }
    } else {
      history.push({
        pathname: "/",
        state: { response: true, message: "Account verified and logged in successfully" },
      });
    }
  };

  const handleSkipVerification = async () => {
    setLoading(true);
    try {
      const resp = await axios.post<FetchResponse<{ message: string; refresh_token?: string; email_verified?: boolean }>>(
        API_ENDPOINT + "/skip-email-verification",
        {
          email: email,
          password: password || undefined, // Include password if available (for login flow)
        }
      );
      const re: any = resp.data;
      
      setMessages({
        type: "success",
        message: re?.message || "Email verification skipped successfully!",
      });
      
      if (onVerifySuccess) {
        onVerifySuccess(re);
      } else {
        // Handle redirect based on mode
        const responseData = re?.data || re;
        if ((mode === 'login' || mode === 'login_otp') && responseData?.refresh_token) {
          // User is logged in, redirect
          handleLoginRedirect(redirectUrl);
        } else if ((mode === 'login' || mode === 'login_otp') && password) {
          // Try login after skipping verification
          tryLoginAfterVerification();
        } else if (mode === 'signup') {
          // For signup, always try to log in user after skip
          if (responseData?.refresh_token) {
            // User is logged in after signup skip
            handleSignupRedirect(redirectUrl);
          } else {
            // Try to login user after signup skip
            // This should not happen normally, but handle it gracefully
            handleSignupRedirect(redirectUrl);
          }
        } else {
          // Email verification skipped but need to login (for login mode without password)
          history.push({
            pathname: "/login",
            search: currentSearch,
            state: { 
              response: true, 
              message: "Email verification skipped temporarily. Please login to continue.",
              verifiedEmail: email
            },
          });
        }
      }
    } catch (error: any) {
      let errorMessage = "Unable to skip email verification. Please try again.";
      
      if (error.response?.status === 404) {
        errorMessage = error.response?.data?.message || "User not found. Please check your email address.";
      } else if (error.response?.status === 401) {
        errorMessage = error.response?.data?.message || "Invalid password. Please try again.";
      } else if (error.response?.status === 500) {
        errorMessage = error.response?.data?.message || "Something went wrong on our end. Please try again in a moment.";
      } else if (!error.response) {
        errorMessage = "Unable to connect. Please check your internet connection and try again.";
      } else if (error.response?.data?.error?.[0]?.form_level_error) {
        errorMessage = error.response.data.error[0].form_level_error;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      
      setMessages({
        type: "error",
        message: errorMessage,
      });
      
      if (onVerifyError) {
        onVerifyError(error);
      }
    }
    setLoading(false);
  };

  useEffect(() => {
    messages.message ? setShowAlert(true) : setShowAlert(false);
  }, [messages]);

  const isButtonDisabled = () => {
    return otp.length !== 4 || isLoading;
  };

  const handleSendOtp = async () => {
    // Prevent sending OTP if timer is still running
    if (delay > 0) {
      return;
    }
    await sendOtp();
  };

  const handleUpdateEmailAndResend = async () => {
    if (!tempEmail || !/^\S+@\S+\.\S+$/.test(tempEmail)) {
      setMessages({
        type: "error",
        message: "Please enter a valid email address.",
      });
      return;
    }
    
    // If email hasn't changed, just close editing
    if (tempEmail === email) {
      setIsEditingEmail(false);
      return;
    }

    setLoading(true);
    try {
      // Update email in database first (for signup flow)
      if (mode === 'signup') {
        try {
          await axios.put(
            API_ENDPOINT + "/update-email",
            {
              old_email: email,
              new_email: tempEmail
            }
          );
        } catch (updateError: any) {
          // If update fails, still allow OTP resend but show warning
          if (updateError.response?.status === 400) {
            setMessages({
              type: "error",
              message: updateError.response?.data?.message || "Email already in use. Please use a different email.",
            });
            setShowAlert(true);
            setLoading(false);
            return;
          }
          // For other errors, log but continue
          console.warn("Failed to update email in database:", updateError);
        }
      }

      // Send OTP to new email first (before updating parent email to avoid re-render issues)
      try {
        await sendOtp(tempEmail);
        
        // Update email in frontend after OTP is sent successfully
        if (onEmailChange) {
          onEmailChange(tempEmail);
        }
        
        // Ensure otpSent is true to show OTP input fields (sendOtp already sets this, but ensure it)
        setOtpSent(true);
        setIsEditingEmail(false);
        
        setMessages({
          type: "success",
          message: "Email updated and OTP sent to new email address.",
        });
        setShowAlert(true);
      } catch (otpError: any) {
        // If OTP send fails, still update email but show error
        if (onEmailChange) {
          onEmailChange(tempEmail);
        }
        setIsEditingEmail(false);
        setLoading(false);
        setMessages({
          type: "error",
          message: otpError?.response?.data?.message || "Failed to send OTP. Please try again.",
        });
        setShowAlert(true);
        return;
      }
    } catch (error: any) {
      setLoading(false);
      setMessages({
        type: "error",
        message: error?.response?.data?.message || "Failed to update email. Please try again.",
      });
      setShowAlert(true);
    } finally {
      setLoading(false);
    }
  };

  // Show skip button only for signup or login (unverified), NOT for sign-in code (login_otp)
  const shouldShowSkip = mode === 'signup' || (mode === 'login' && !otpAlreadySent);
  
  return (
    <Container>
      {shouldShowSkip && (
        <SkipTextButton
          type="button"
          onClick={handleSkipVerification}
          disabled={isLoading}
        >
          Skip
        </SkipTextButton>
      )}
      {!otpSent ? (
        <>
          <DescriptionText>
            Please click the button below to receive a 4-digit OTP to <EmailHighlight>{email}</EmailHighlight>
          </DescriptionText>
          {showAlert && (
            <AlertDiv
              className={messages?.type === "success" ? "alert alert-success" : "alert alert-danger"}
              role="alert"
            >
              {messages?.message}
            </AlertDiv>
          )}
          <div style={{ marginTop: "30px" }}>
            <StyledButton
              type="button"
              onClick={handleSendOtp}
              disabled={isLoading}
              isLoading={isLoading}
            >
              Send OTP to Email
            </StyledButton>
          </div>
        </>
      ) : (
        <>
          <DescriptionText>
            {isEditingEmail && mode === 'signup' ? (
              <EditEmailWrapper>
                <EmailInput 
                  type="email"
                  value={tempEmail}
                  onChange={(e) => setTempEmail(e.target.value)}
                  placeholder="Enter new email address"
                  autoFocus
                />
                <div style={{ marginTop: '10px' }}>
                  <EditActionLink onClick={handleUpdateEmailAndResend} style={{ color: '#3f52fd', fontWeight: '600' }}>
                    Update & Resend OTP
                  </EditActionLink>
                  <span style={{ margin: '0 8px', color: '#93acbd' }}>|</span>
                  <EditActionLink onClick={() => { setIsEditingEmail(false); setTempEmail(email); }}>
                    Cancel
                  </EditActionLink>
                </div>
              </EditEmailWrapper>
            ) : (
              <>
                We've sent a 4-digit OTP to <EmailHighlight>{email}</EmailHighlight>
                {mode === 'signup' && (
                  <EditActionLink onClick={() => setIsEditingEmail(true)} style={{ marginLeft: '8px' }}>
                    (Edit)
                  </EditActionLink>
                )}
              </>
            )}
          </DescriptionText>
          {showAlert && (
            <AlertDiv
              className={messages?.type === "success" ? "alert alert-success" : "alert alert-danger"}
              role="alert"
            >
              {messages?.message}
            </AlertDiv>
          )}
          {!isEditingEmail && (
            <div style={{ marginTop: "30px" }}>
              <CustomOtpInput
                value={otp}
                onChange={setOtp}
                numInputs={4}
              />
              <ResendContainer>
                <Flex>
                  <Text> Didn't receive the OTP?</Text>
                  <OtpReSend 
                    onClick={delay > 0 ? undefined : handleSendOtp} 
                    style={{ 
                      cursor: delay > 0 ? 'not-allowed' : 'pointer', 
                      opacity: delay > 0 ? 0.5 : 1,
                      pointerEvents: delay > 0 ? 'none' : 'auto'
                    }}
                  >
                    RE-SEND
                  </OtpReSend>
                </Flex>
                <Flex>
                  <TimerDisplay>
                    {timeDisplay}
                  </TimerDisplay>
                </Flex>
              </ResendContainer>
              <StyledButton
                type="button"
                onClick={handleVerifyOtp}
                disabled={isButtonDisabled()}
                isLoading={isLoading}
              >
                {(mode === 'login' || mode === 'login_otp') ? 'Verify OTP & Login' : 'Verify & Continue'}
              </StyledButton>
            </div>
          )}
        </>
      )}
    </Container>
  );
};

export default EmailOtpVerification;

const Container = styled.div`
  width: 100%;
  position: relative;
`;

const EditEmailWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 10px;
`;

const EmailInput = styled.input`
  padding: 8px 12px;
  border: 1.2px solid #88b9db;
  border-radius: 8px;
  width: 100%;
  max-width: 300px;
  font-size: 14px;
  text-align: center;
  
  &:focus {
    outline: none;
    border-color: #3f52fd;
    background: #f0f4ff;
  }
`;

const EditActionLink = styled.span`
  font-size: 13px;
  color: #3f52fd;
  cursor: pointer;
  text-decoration: none;
  font-weight: 500;
  
  &:hover {
    text-decoration: underline;
  }
`;

const OtpContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 16px;
  margin-top: 40px;
  margin-bottom: 30px;
  @media (max-width: 576px) {
    gap: 12px;
  }
`;

const OtpInputField = styled.input`
  width: 64px !important;
  height: 64px;
  padding: 0;
  font-weight: 700;
  font-size: 24px;
  color: #3f52fd;
  background: #ffffff;
  border: 2px solid #e0e7ff;
  border-radius: 12px;
  text-align: center;
  font-family: inherit;
  transition: all 0.2s ease;

  &:focus {
    background: #f0f4ff;
    border: 2px solid #3f52fd;
    border-radius: 12px;
    outline: none;
    box-shadow: 0 0 0 3px rgba(63, 82, 253, 0.1);
    transform: scale(1.05);
  }

  &:hover {
    border-color: #3f52fd;
  }

  @media (max-width: 576px) {
    width: 52px !important;
    height: 52px;
    font-size: 20px;
  }
`;

const ResendContainer = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
  margin-top: 20px;
`;

const OtpReSend = styled.span`
  font-weight: 600;
  color: #3f52fd;
  cursor: pointer;
  font-size: 14px;
  margin-left: 5px;
  transition: all 0.2s ease;
  
  &:hover {
    color: #2d3fd8;
    text-decoration: underline;
  }
  
  &:active {
    transform: scale(0.95);
  }
`;

const TimerDisplay = styled.span`
  font-weight: 600;
  color: #93acbd;
  font-size: 14px;
  font-variant-numeric: tabular-nums;
`;

const StyledButton = styled(Button)`
  background: #3f52fd;
  border-radius: 10px;
  margin-top: 3rem;
  @media (max-width: 576px) {
    margin-top: 5rem;
    height: 50px !important;
  }
`;

const SkipTextButton = styled.button`
  background: transparent;
  border: none;
  color: #93acbd;
  font-weight: 500;
  font-size: 14px;
  cursor: pointer;
  padding: 8px 12px;
  position: absolute;
  top: 0;
  right: 0;
  transition: all 0.2s ease;
  
  &:hover:not(:disabled) {
    color: #64748b;
    text-decoration: underline;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  @media (max-width: 576px) {
    margin-top: 1.5rem;
    height: 50px;
  }
`;

const Text = styled.p`
  font-weight: 500;
  font-size: 14px;
  line-height: 21px;
  color: #93acbd;
  margin: 0;
  @media (max-width: 576px) {
    font-size: 12px;
  }
`;

const DescriptionText = styled.p`
  font-weight: 400;
  font-size: 15px;
  line-height: 24px;
  color: #64748b;
  margin: 20px 0 0 0;
  text-align: center;
  @media (max-width: 576px) {
    font-size: 13px;
  }
`;

const EmailHighlight = styled.strong`
  color: #3f52fd;
  font-weight: 600;
`;

const AlertDiv = styled.div`
  margin-top: 15px;
  margin-bottom: 15px;
`;

